import type {
  CancelImpactSubmissionsQuery,
  CancelImpactSubmissionsQueryVariables,
} from "generated-graphql/graphql";

import { useLazyQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { useCallback, useEffect, useRef, useState } from "react";

const CANCEL_IMPACT_SUBMISSIONS = gql(`
  query CancelImpactSubmissions($id: ID!) {
    payment(id: $id) {
      id
      submissions {
        id
        submission {
          id
          title
          status
        }
      }
    }
  }
`);

/** A submission funded by a payment, shown in the pre-cancel warning. Lives
 * here (with the other Payment components) rather than inside the generic
 * ConfirmDialog primitive, which must stay domain-neutral. */
export type FundedSubmission = {
  id: string;
  title: string | null;
  status: string;
};

/** How long (ms) to wait for the impact query before surfacing a timeout error. */
export const CANCEL_IMPACT_TIMEOUT_MS = 10_000;

type SubmissionPaymentRow = NonNullable<
  NonNullable<CancelImpactSubmissionsQuery["payment"]["submissions"]>[number]
>;

export function useCancelImpactSubmissions(): [
  (paymentId: string) => void,
  { data: FundedSubmission[]; loading: boolean; error: string | null },
] {
  const [run, { data, loading, error: apolloError }] = useLazyQuery<
    CancelImpactSubmissionsQuery,
    CancelImpactSubmissionsQueryVariables
    // Pre-cancel warning must reflect the funding state at the moment of
    // decision — never a stale cached list from a prior payment.
  >(CANCEL_IMPACT_SUBMISSIONS, { fetchPolicy: "network-only" });

  const [timedOut, setTimedOut] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable identity: timerRef is a ref, so there are no reactive deps.
  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Unmount cleanup: cancel any pending timer so setState never fires on a
  // dead component. This is the sole unmount guard — it runs once on teardown.
  useEffect(() => () => clearTimer(), [clearTimer]);

  // Clear the timer and reset the timedOut flag when the query actually
  // SETTLES (data or error present). Gated on settlement — not merely on
  // !loading — to avoid the disarm race where the effect runs in the idle
  // render frame between fire() and Apollo flipping loading→true, which would
  // disarm the timer before the request even starts.
  useEffect(() => {
    if (data !== undefined || apolloError !== undefined) {
      clearTimer();
      setTimedOut(false);
    }
  }, [data, apolloError, clearTimer]);

  const funded: FundedSubmission[] = (data?.payment?.submissions ?? []).map(
    (sp: SubmissionPaymentRow) => ({
      id: sp.submission.id,
      title: sp.submission.title ?? null,
      status: sp.submission.status,
    })
  );

  const errorMessage: string | null = (() => {
    if (timedOut) return "The check timed out. Please try again before cancelling.";
    if (apolloError)
      return "Could not check funded submissions. Please try again before cancelling.";
    return null;
  })();

  // A hung query keeps apollo `loading` true forever; once we've timed out we
  // must report loading=false so the consumer renders the error (and releases
  // the blocked confirm) instead of stranding it on a spinner.
  const isLoading = loading && !timedOut;

  const fire = (paymentId: string) => {
    setTimedOut(false);
    clearTimer();
    timerRef.current = setTimeout(() => {
      setTimedOut(true);
      timerRef.current = null;
    }, CANCEL_IMPACT_TIMEOUT_MS);
    run({ variables: { id: paymentId } });
  };

  return [fire, { data: funded, loading: isLoading, error: errorMessage }];
}
